import { FLAT_ITEM_TYPE } from "./flatPreviewTypes";

/**
 * Adapts the flat `Survey/GetPreviewDataFlat` payload back into the nested shape that
 * `Survey/GetPreviewData` returned, so the renderer components keep working untouched.
 *
 * The mapping is deliberately parity-only: every field is produced to match what the old endpoint
 * actually sent for the same survey, including quirks (see the notes on individual fields). Fields
 * the flat endpoint adds and the old one never had - `range`, `maxChoices`, `index`, `depth`,
 * `titleLanguageId` on the node, `maxScore` on questions - are NOT surfaced, because components
 * already read `range` and `maxChoices` and would start rendering differently the moment they
 * appear. Surface them as a separate, deliberate change once the endpoint swap is verified.
 */

const ITEM_LABEL = FLAT_ITEM_TYPE;

/**
 * The old endpoint sent label rows straight out of the DB, so they carried bookkeeping columns.
 * The flat endpoint resolves labels server side and does not return the row identity, so these
 * are filled with the neutral values the old payload always had in preview responses. Nothing in
 * the renderer reads them apart from `choiceLabels[].choiceId`, which is the value of a select
 * option and therefore mapped explicitly.
 */
const LABEL_ROW_DEFAULTS = {
   id: null,
   createdAt: null,
   updatedAt: null,
   isDeleted: false
};

/**
 * Builds the single-entry `questionLabels` array the renderer expects.
 *
 * The old payload could hold one row per language and the renderer picks
 * `find(languageId === toLanguage) ?? [0]`. The flat endpoint already resolved the requested
 * language (falling back to the base language), so one row carrying the language it actually came
 * from resolves to the same text through either branch.
 *
 * `titleLanguageId === null` means no label row existed at all and `title` fell back to the
 * question's raw Title column - the old payload sent an empty array there, so we do too.
 */
function toQuestionLabels(item) {
   if (!item.titleLanguageId) {
      return [];
   }

   return [
      {
         questionId: item.id,
         question: null,
         text: item.title,
         languageId: item.titleLanguageId,
         ...LABEL_ROW_DEFAULTS
      }
   ];
}

/**
 * Hints and constraint messages follow the same requested-language-then-base-language rule as the
 * title, but the flat payload does not say which language actually won. They are tagged with the
 * response's effective `languageId`: the renderer either matches on it or falls through to its
 * `[0]` branch, and both branches read the one resolved text - so the rendered string is the same
 * whichever language it came from.
 */
function toTranslatedRows(questionId, text, languageId) {
   if (text === null || text === undefined) {
      return [];
   }

   return [
      {
         questionId,
         question: null,
         text,
         languageId: languageId ?? null,
         ...LABEL_ROW_DEFAULTS
      }
   ];
}

function toChoiceLabels(choice) {
   if (choice.label === null || choice.label === undefined) {
      return [];
   }

   return [
      {
         // Read as the value of a select option (`choiceLabels[0].choiceId`), so it has to be the
         // choice's own id - not the label row's.
         choiceId: choice.id,
         secondChoiceId: null,
         text: choice.label,
         languageId: choice.labelLanguageId ?? null,
         choice: null,
         secondChoice: null,
         ...LABEL_ROW_DEFAULTS
      }
   ];
}

function toSecondChoiceLabels(secondChoice) {
   if (secondChoice.label === null || secondChoice.label === undefined) {
      return [];
   }

   return [
      {
         choiceId: null,
         secondChoiceId: secondChoice.id,
         text: secondChoice.label,
         languageId: secondChoice.labelLanguageId ?? null,
         choice: null,
         secondChoice: null,
         ...LABEL_ROW_DEFAULTS
      }
   ];
}

/**
 * Cascading selects: the old payload nested the filtering choice under `filterChoice` and its
 * questions under `filterChoice.questionsResponse`, which is what the choice-filter evaluator
 * reads (`option.choiceFilters[i].filterChoice.name` and `.questionsResponse[0].name`).
 */
function toChoiceFilters(choice) {
   return (choice.choiceFilters ?? []).map((choiceFilter) => ({
      choiceId: choice.id,
      choice: null,
      choiceFilterId: choiceFilter.choiceFilterId ?? null,
      filterChoice: {
         listName: choiceFilter.listName ?? null,
         name: choiceFilter.name ?? null,
         itemType: null,
         objectName: null,
         filter_Param_Name: null,
         filter_Param_Value: null,
         choiceLabels: null,
         choiceFilters: null,
         questionsResponse: choiceFilter.questions ?? [],
         order: 0,
         displayAsAnswerChoice: null,
         displayAsComment: null,
         isOther: null,
         noneOfTheAbove: null,
         emoji: null,
         assignScore: null,
         ...LABEL_ROW_DEFAULTS
      }
   }));
}

function toSelectQuestionChoices(item) {
   return (item.choices ?? []).map((choice) => ({
      listName: choice.listName ?? null,
      name: choice.name,
      itemType: choice.itemType ?? null,
      objectName: choice.objectName ?? null,
      filter_Param_Name: choice.filter_Param_Name ?? null,
      filter_Param_Value: choice.filter_Param_Value ?? null,
      choiceLabels: toChoiceLabels(choice),
      choiceFilters: toChoiceFilters(choice),
      questionsResponse: null,
      order: choice.order,
      displayAsAnswerChoice: choice.displayAsAnswerChoice ?? null,
      displayAsComment: choice.displayAsComment ?? null,
      isOther: choice.isOther ?? null,
      noneOfTheAbove: choice.noneOfTheAbove ?? null,
      emoji: choice.emoji ?? null,
      assignScore: null,
      id: choice.id,
      createdAt: null,
      updatedAt: null,
      isDeleted: false
   }));
}

function toSecondChoices(item) {
   // Unlike a choice, the old payload's second choice carried no audit columns
   return (item.secondChoices ?? []).map((secondChoice) => ({
      id: secondChoice.id,
      name: secondChoice.name,
      // Dense, from 1, per question. The builder's diff sorts on it rather than on array position,
      // which can come from a stale read.
      order: secondChoice.order,
      questionId: item.id,
      question: null,
      choiceLabels: toSecondChoiceLabels(secondChoice)
   }));
}

/**
 * @param {import("./flatPreviewTypes").FlatSurveyItem} item
 * @param {string|null} languageId Effective language of the response, used to tag resolved texts.
 */
function toQuestionNode(item, languageId) {
   return {
      itemLabel: ITEM_LABEL.QUESTION,
      // The old payload declared these on the node as well but never populated them - the real
      // values live inside `surveyItem`. Kept null so the components behave exactly as today.
      relevant: null,
      repeat_count: null,
      repeatGroup: null,
      id: item.id,
      surveyItem: {
         id: item.id,
         // The old `title` was the raw xlsform Title column, which the importer always fills with
         // the technical name - not the translated label. The translated label is `questionLabels`.
         title: item.name,
         name: item.name,
         description: item.description ?? null,
         questionType: item.questionType,
         order: item.order,
         surveyOrder: item.surveyOrder ?? null,
         surveyId: item.surveyId ?? null,
         sectionId: item.parentSectionId ?? null,
         isRequired: item.isRequired,
         choiceFilter: item.choiceFilter ?? null,
         accuracyThreshold: item.accuracyThreshold ?? null,
         default: item.default ?? null,
         given_name: item.given_name ?? null,
         repeat_Count: null,
         appearance: item.appearance ?? null,
         trigger: item.trigger ?? null,
         relevant: item.relevant ?? null,
         constraint: item.constraint ?? null,
         parameters: item.parameters ?? null,
         calculation: item.calculation ?? null,
         hasEmoji: item.hasEmoji ?? null,
         imageUrl: item.imageUrl ?? null,
         // Preview never populated skip logics; the renderer derives them from `relevant`.
         skipLogics: null,
         questionLabels: toQuestionLabels(item),
         surveyQuestionHints: toTranslatedRows(item.id, item.hint, languageId),
         selectQuestionChoices: toSelectQuestionChoices(item),
         secondChoices: toSecondChoices(item),
         constraintMessages: toTranslatedRows(item.id, item.constraintMessage, languageId),
         // Always null in preview - answers are only loaded by the submission viewer.
         answers: null
      },
      sectionLabel: null,
      section: null,
      innerItems: null,
      answer: null
   };
}

/**
 * @param {import("./flatPreviewTypes").FlatSurveyItem} item
 */
function toSectionNode(item) {
   return {
      itemLabel: ITEM_LABEL.SECTION,
      // The old payload declared `relevant` on the node too but never populated it - the section's
      // own expression lives in `surveyItem.relevant`, which is where the renderer reads it from.
      relevant: null,
      // These two, in contrast, ARE read off the node: Section.jsx decides whether a section is a
      // repeat group from `repeat_count`, and reads the submitted instance count from
      // `repeatGroup`. Leaving them null makes a repeat group render its body once unconditionally,
      // instead of `repeat_count` times - and not at all when that evaluates to 0.
      repeat_count: item.repeat_count ?? null,
      repeatGroup: item.repeatGroup ?? null,
      id: item.id,
      surveyItem: {
         surveyId: item.surveyId ?? null,
         sectionId: item.parentSectionId ?? null,
         name: item.name,
         relevant: item.relevant ?? null,
         repeat_count: item.repeat_count ?? null,
         // A section renders `sectionLabel.description` when it has a label and its own
         // `description` otherwise. The flat endpoint already collapsed the two into one resolved
         // string, so both slots get it and either branch renders the same text.
         description: item.description ?? null,
         order: item.order,
         sectionOrder: item.order,
         // Nothing reads a section's `surveyOrder`: `order` is the position, and the nested
         // endpoint reports `surveyOrder` only for a top-level section (null inside another
         // section), so it could not be sorted on anyway.
         surveyOrder: null,
         isChildSection: item.isChildSection ?? false,
         maxScore: item.maxScore ?? null,
         repeatGroup: item.repeatGroup ?? null,
         skipLogics: null
      },
      // `title === null` means the section has no label in the requested or the base language,
      // which is exactly when the old payload sent `sectionLabel: null`.
      sectionLabel:
         item.title === null || item.title === undefined
            ? null
            : {
                 id: null,
                 name: item.title,
                 description: item.description ?? null,
                 languageId: item.titleLanguageId ?? null
              },
      section: null,
      innerItems: [],
      answer: null
   };
}

/**
 * Index of the first item after `sectionIndex` that is not part of that section's subtree.
 *
 * A section's whole subtree is the consecutive run of items right after it whose `depth` is
 * greater than the section's own - the backend emits depth-first, so this is a single linear scan.
 *
 * @param {import("./flatPreviewTypes").FlatSurveyItem[]} items
 * @param {number} sectionIndex
 * @returns {number}
 */
export function getSubtreeEnd(items, sectionIndex) {
   const depth = items[sectionIndex].depth;
   let end = sectionIndex + 1;

   while (end < items.length && items[end].depth > depth) {
      end++;
   }

   return end;
}

/**
 * The consecutive run of items belonging to the section at `sectionIndex`, excluding the section.
 *
 * @param {import("./flatPreviewTypes").FlatSurveyItem[]} items
 * @param {number} sectionIndex
 * @returns {import("./flatPreviewTypes").FlatSurveyItem[]}
 */
export function getSubtree(items, sectionIndex) {
   return items.slice(sectionIndex + 1, getSubtreeEnd(items, sectionIndex));
}

/**
 * Rebuilds the nested `surveyItems` tree from the flat, ordered list in one pass.
 *
 * The array order is the render order and is never re-sorted: `order` is per-parent and the same
 * value legitimately appears on both a section and a question inside the same parent.
 *
 * @param {import("./flatPreviewTypes").FlatSurveyItem[]} items
 * @param {string|null} [languageId] Effective language of the response.
 * @returns {Object[]} nodes in the shape `Survey/GetPreviewData` returned
 */
export function buildPreviewTreeFromFlat(items, languageId = null) {
   if (!Array.isArray(items)) {
      return [];
   }

   const roots = [];
   const sectionsById = new Map();
   /** Open sections, deepest last, so a child can find its parent by depth alone. */
   const openSections = [];

   items.forEach((item) => {
      if (!item) {
         return;
      }

      const isSection = item.itemType === ITEM_LABEL.SECTION;
      const node = isSection ? toSectionNode(item) : toQuestionNode(item, languageId);
      const depth = typeof item.depth === "number" ? item.depth : 0;

      while (openSections.length && openSections[openSections.length - 1].depth >= depth) {
         openSections.pop();
      }

      // `parentSectionId` is authoritative and its section is always already mapped (depth-first
      // emission). The depth stack is the fallback for a parent id we have not seen.
      const parent =
         (item.parentSectionId && sectionsById.get(item.parentSectionId)) ||
         openSections[openSections.length - 1]?.node ||
         null;

      if (parent) {
         parent.innerItems.push(node);
      } else {
         roots.push(node);
      }

      if (isSection) {
         sectionsById.set(item.id, node);
         openSections.push({ depth, node });
      }
   });

   return roots;
}

/**
 * Puts the language the response was resolved in first, keeping the rest in their existing order.
 *
 * The language picker renders `surveyLanguageMaps` straight into its option list, so the array
 * order is the order the user sees. The old endpoint returned the requested language first (and
 * therefore the base language when nothing was requested, since that is what it falls back to);
 * the flat one returns whatever order the database gives, which usually puts a translation ahead
 * of the base language and reshuffles the picker.
 *
 * @param {import("./flatPreviewTypes").FlatSurveyLanguageMap[]} surveyLanguageMaps
 * @param {string|null} languageId Effective language of the response.
 */
export function orderRequestedLanguageFirst(surveyLanguageMaps, languageId) {
   if (!Array.isArray(surveyLanguageMaps)) {
      return surveyLanguageMaps;
   }

   let index = surveyLanguageMaps.findIndex((language) => language?.languageId === languageId);

   if (index < 0) {
      index = surveyLanguageMaps.findIndex((language) => language?.isBaseLanguage);
   }

   if (index <= 0) {
      return surveyLanguageMaps;
   }

   return [surveyLanguageMaps[index], ...surveyLanguageMaps.filter((_, i) => i !== index)];
}

/**
 * Adapts a whole `data` object into the old endpoint's `data` shape.
 *
 * The header is already a superset of the old one, so it is passed through as-is (keeping the new
 * `languageId` / `baseLanguageId` / `surveyLanguageMaps[].code`); only `items` is replaced by the
 * rebuilt `surveyItems` tree, and the language list is reordered to match the old endpoint.
 *
 * @param {import("./flatPreviewTypes").FlatPreviewData} data
 * @returns {Object|null}
 */
export function adaptFlatPreviewData(data) {
   if (!data) {
      return null;
   }

   const { items, ...survey } = data;

   return {
      ...survey,
      surveyLanguageMaps: orderRequestedLanguageFirst(
         data.surveyLanguageMaps,
         data.languageId ?? null
      ),
      surveyItems: buildPreviewTreeFromFlat(items, data.languageId ?? null)
   };
}
